import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import React, { ReactNode, useContext, useEffect, useState } from 'react';
import { ErrorBoundary } from 'src/components/system';
import {
  Box,
  Flex,
  IconButton,
  useDisclosure,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerBody,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react';
import { GoSidebarExpand, GoSidebarCollapse } from 'react-icons/go';
import { AdminDashboardProvider, useAdminDashboard } from 'src/contexts/adminDashboard';
import AdminRouteSidebar from 'src/components/molecules/admin-route-sidebar';
import { MarketplaceContext } from 'src/contexts/marketplace';
import { useRouter } from 'next/router';
import { useAuth } from 'src/utils/auth';
import { CoverLoader } from 'src/components/atoms';
import { ADMIN_LOGIN_PATHS } from 'src/constants/marketplace';
import AdminRouteRedirect from 'src/components/organisms/admin-route-redirect';
import HelpButton from 'src/components/atoms/help-button';

interface AdminRouteLayoutProps {
  children: ReactNode;
}

const AdminRouteLayoutContent = ({ children }: AdminRouteLayoutProps) => {
  const { marketplace, marketplaceSlug } = useContext(MarketplaceContext);
  const router = useRouter();
  const { isAuthenticated, userLoaded } = useAuth();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { currentPage, pageName } = useAdminDashboard();
  const isDesktop = useBreakpointValue({ base: false, lg: true });
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  // if the marketplace slug is admin, we have loaded the user but they have no affiliate marketplace
  const noAdminMarketplace = marketplaceSlug === 'admin';
  const affiliateLoaded = userLoaded && isAuthenticated && marketplace && marketplace?.id;

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (userLoaded && !isAuthenticated && !ADMIN_LOGIN_PATHS.includes(router.pathname)) {
      // user not logged in
      router.push('/login');
    }
  }, [userLoaded, isAuthenticated, router]);

  // Close mobile drawer when resizing to desktop
  useEffect(() => {
    if (isDesktop) {
      onClose();
      setIsSidebarCollapsed(false);
    } else {
      setIsSidebarCollapsed(true);
    }
  }, [isDesktop]);

  const handleMenuToggle = () => {
    if (isDesktop) {
      setIsSidebarCollapsed(!isSidebarCollapsed);
    } else {
      onOpen();
    }
  };

  return (
    <Flex h="100vh" overflow="hidden">
      {/* Desktop Sidebar */}
      {isMounted && !isSidebarCollapsed && affiliateLoaded && (
        <Box display={{ base: 'none', lg: 'block' }} flexShrink={0}>
          <AdminRouteSidebar currentPage={currentPage} />
        </Box>
      )}

      {/* Main Content Area */}
      <Box flex="1" overflowX="hidden" overflowY="auto" position="relative">
        {/* Top Bar */}
        <Flex
          align="center"
          gap={3}
          px={{ base: 4, md: 6 }}
          py={3}
          borderBottom="1px solid"
          borderColor="gray.200"
          bg="white"
          position="sticky"
          top={0}
          zIndex={10}
        >
          <IconButton
            aria-label="Toggle navigation"
            icon={
              isSidebarCollapsed ? (
                <GoSidebarCollapse size="24px" />
              ) : (
                <GoSidebarExpand size="24px" />
              )
            }
            onClick={handleMenuToggle}
            variant="ghost"
            size="md"
          />
          <Text fontSize="lg" fontWeight="semibold" color="gray.700">
            {pageName}
          </Text>
          <Link
            href="https://withloma.com/"
            target="_blank"
            rel="noopener noreferrer"
            style={{ marginLeft: 'auto', paddingRight: '4px' }}
          >
            <Box borderRadius="lg" overflow="hidden">
              <Image src="/images/loma-admin.ico" alt="Loma Admin Logo" width={32} height={32} />
            </Box>
          </Link>
        </Flex>

        {/* Page Content */}
        <Box>
          {!isMounted || !userLoaded || !marketplaceSlug ? (
            <CoverLoader context="AdminRouteLayout:LoadingUserAndMarketplace" />
          ) : noAdminMarketplace &&
            isAuthenticated &&
            !ADMIN_LOGIN_PATHS.includes(router.pathname) ? (
            <AdminRouteRedirect />
          ) : (
            <ErrorBoundary>{children}</ErrorBoundary>
          )}
        </Box>
      </Box>

      {/* Mobile Drawer */}
      {isMounted && affiliateLoaded && (
        <Drawer isOpen={isOpen} placement="left" onClose={onClose} size="xs">
          <DrawerOverlay />
          <DrawerContent maxW="260px" overflow="visible">
            <DrawerBody p={0} overflow="visible">
              <AdminRouteSidebar currentPage={currentPage} onNavigate={onClose} />
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      )}
      {isMounted && affiliateLoaded && <HelpButton />}
    </Flex>
  );
};

const AdminRouteLayout = ({ children }: AdminRouteLayoutProps) => {
  return (
    <AdminDashboardProvider>
      <Head>
        <link rel="icon" type="image/png" sizes="48x48" href={'/images/loma-admin.ico'} />
        <link rel="shortcut icon" href={'/images/loma-admin.ico'} />
        <link rel="apple-touch-icon" sizes="48x48" href={'/images/loma-admin.ico'} />
        <meta name="apple-mobile-web-app-title" content={'Loma Admin'} />
      </Head>
      <AdminRouteLayoutContent>{children}</AdminRouteLayoutContent>
    </AdminDashboardProvider>
  );
};

export default AdminRouteLayout;
